/*
  Typewriter heading
  ------------------
  Drives any element marked with [data-typewriter]. Expected markup:

    <h2 data-typewriter data-phrases='["one","two"]'>
      <span class="tw-heading__ghost">one</span>   <- reserves layout height
      <span class="tw-heading__ghost">two</span>
      <span class="tw-heading__live"><span class="tw-heading__chars"></span><span class="tw-heading__caret"></span></span>
      <span class="tw-heading__sr">one</span>      <- what screen readers announce
    </h2>

  The ghost spans are grid-stacked with the live span so the box is always as
  tall as the longest phrase. Without them the section would reflow on every
  keystroke, which is very visible on mobile where the heading wraps to 3-4
  lines.
*/
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function toInt(value, fallback) {
    var n = parseInt(value, 10);
    return isNaN(n) || n < 0 ? fallback : n;
  }

  function setup(el) {
    el.setAttribute('data-tw-ready', '');

    var phrases;
    try {
      phrases = JSON.parse(el.getAttribute('data-phrases') || '[]');
    } catch (e) {
      phrases = [];
    }
    phrases = phrases.filter(function (p) {
      return typeof p === 'string' && p.trim() !== '';
    });
    if (!phrases.length) return;

    var chars = el.querySelector('.tw-heading__chars');
    if (!chars) return;

    // Someone who asked their OS for less motion gets the finished text, not
    // a looping animation.
    if (reduced.matches) {
      chars.textContent = phrases[0];
      el.classList.add('tw-heading--static');
      return;
    }

    var typeMs = toInt(el.getAttribute('data-type-speed'), 55);
    var deleteMs = toInt(el.getAttribute('data-delete-speed'), 28);
    var holdMs = toInt(el.getAttribute('data-hold'), 1800);
    var loop = el.getAttribute('data-loop') !== 'false';
    var gapMs = 350;

    var index = 0;
    var pos = 0;
    var deleting = false;
    var timer = null;
    var running = false;
    var finished = false;

    function schedule(ms) {
      timer = window.setTimeout(step, ms);
    }

    function step() {
      var full = phrases[index];

      if (!deleting) {
        pos++;
        chars.textContent = full.slice(0, pos);

        if (pos >= full.length) {
          var isLast = index === phrases.length - 1;
          if (!loop && isLast) {
            finished = true;
            running = false;
            el.classList.add('tw-heading--done');
            return;
          }
          deleting = true;
          return schedule(holdMs);
        }
        return schedule(typeMs);
      }

      pos--;
      chars.textContent = full.slice(0, pos);

      if (pos <= 0) {
        deleting = false;
        index = (index + 1) % phrases.length;
        return schedule(gapMs);
      }
      schedule(deleteMs);
    }

    function start() {
      if (running || finished) return;
      running = true;
      schedule(typeMs);
    }

    function stop() {
      running = false;
      window.clearTimeout(timer);
    }

    // Don't animate offscreen: the effect should begin when the section is
    // actually scrolled into view, and shouldn't burn timers before that.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) start();
            else stop();
          });
        },
        { threshold: 0.25 }
      );
      io.observe(el);
      el._twObserver = io;
    } else {
      start();
    }

    // Background tabs throttle timers, which desyncs the animation.
    function onVisibility() {
      if (document.hidden) stop();
      else if (isInView(el)) start();
    }
    document.addEventListener('visibilitychange', onVisibility);

    el._twStop = function () {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
      if (el._twObserver) el._twObserver.disconnect();
    };
  }

  function isInView(el) {
    var r = el.getBoundingClientRect();
    return r.bottom > 0 && r.top < (window.innerHeight || 0);
  }

  function init(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll('[data-typewriter]:not([data-tw-ready])');
    Array.prototype.forEach.call(nodes, setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor: sections are torn down and re-rendered on every edit.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var nodes = e.target.querySelectorAll('[data-typewriter]');
    Array.prototype.forEach.call(nodes, function (el) {
      if (el._twStop) el._twStop();
    });
  });
})();
